import type {
  Paper,
  PreferenceMapping,
  RankingResult,
  ReviewDecision,
  ReviewStatus,
} from './types';

export interface ExportSummary {
  totalPapers: number;
  exportedRows: number;
  untouchedPreferences: number;
  mappedPreferences: number;
  blankPreferences: number;
  omittedConflicts: number;
}

export interface CsvExport {
  csv: string;
  summary: ExportSummary;
}

const MANUAL_STATUSES = ['interested', 'not_interested', 'uncertain'] as const;
type ManualStatus = (typeof MANUAL_STATUSES)[number];
const STATUS_LABELS: Record<ManualStatus, string> = {
  interested: 'interested',
  not_interested: 'not interested',
  uncertain: 'would review if needed',
};

function csvCell(value: string | number): string {
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

function csv(rows: ReadonlyArray<ReadonlyArray<string | number>>): string {
  return `${rows.map((row) => row.map(csvCell).join(',')).join('\r\n')}\r\n`;
}

function isManualStatus(status: ReviewStatus | undefined): status is ManualStatus {
  return status !== undefined && MANUAL_STATUSES.includes(status as ManualStatus);
}

export function validatePreferenceMapping(mapping: PreferenceMapping): void {
  for (const status of MANUAL_STATUSES) {
    const value = mapping[status];
    if (value !== null && (!Number.isInteger(value) || value < -20 || value > 20)) {
      throw new RangeError(
        `${STATUS_LABELS[status]} preference must be a whole number from -20 to 20, or blank.`,
      );
    }
  }
}

function emptySummary(totalPapers: number): ExportSummary {
  return {
    totalPapers,
    exportedRows: 0,
    untouchedPreferences: 0,
    mappedPreferences: 0,
    blankPreferences: 0,
    omittedConflicts: 0,
  };
}

/**
 * Exports a readable record of every paper and the user's local review.
 */
export function createPersonalCsv(
  papers: readonly Paper[],
  decisions: Readonly<Record<string, ReviewDecision>>,
  rankings: readonly RankingResult[] = [],
): CsvExport {
  const summary = emptySummary(papers.length);
  const rankingsByPaper = new Map(rankings.map((ranking) => [ranking.paperId, ranking]));
  const rows: Array<Array<string | number>> = [
    [
      'paper',
      'title',
      'abstract',
      'topics',
      'track',
      'similarity',
      'matched_profile_items',
      'status',
      'note',
      'original_preference',
    ],
  ];

  for (const paper of papers) {
    const decision = decisions[paper.id];
    const ranking = rankingsByPaper.get(paper.id);
    rows.push([
      paper.id,
      paper.title,
      paper.abstract,
      paper.topics.join('; '),
      paper.track,
      ranking?.score.toFixed(6) ?? '',
      ranking?.matches
        .slice(0, 3)
        .map((match) => `${match.label} (${match.score.toFixed(3)})`)
        .join('; ') ?? '',
      decision?.status === 'uncertain' ? 'would_review_if_needed' : (decision?.status ?? ''),
      decision?.note ?? '',
      paper.preference,
    ]);
    summary.exportedRows += 1;
    summary.untouchedPreferences += 1;
  }

  return { csv: csv(rows), summary };
}

/**
 * Produces a HotCRP preference upload. Unreviewed papers retain their imported
 * preference, while manually reviewed papers use the configured mapping.
 */
export function createHotcrpCsv(
  papers: readonly Paper[],
  decisions: Readonly<Record<string, ReviewDecision>>,
  mapping: PreferenceMapping,
): CsvExport {
  validatePreferenceMapping(mapping);
  const summary = emptySummary(papers.length);
  const rows: Array<Array<string | number>> = [['paper', 'preference']];

  for (const paper of papers) {
    const status = decisions[paper.id]?.status;
    if (status === 'conflict') {
      summary.omittedConflicts += 1;
      continue;
    }

    let preference: string | number = paper.preference;
    if (isManualStatus(status)) {
      const mapped = mapping[status];
      preference = mapped ?? '';
      summary.mappedPreferences += 1;
      if (mapped === null) summary.blankPreferences += 1;
    } else {
      summary.untouchedPreferences += 1;
      if (preference === '') summary.blankPreferences += 1;
    }

    rows.push([paper.id, preference]);
    summary.exportedRows += 1;
  }

  return { csv: csv(rows), summary };
}

export const exportPersonalCsv = createPersonalCsv;
export const exportHotcrpCsv = createHotcrpCsv;

export function selectTopInterestedPapers(
  papers: readonly Paper[],
  decisions: Readonly<Record<string, ReviewDecision>>,
  rankings: readonly RankingResult[],
  limit: number,
): Paper[] {
  if (!Number.isInteger(limit) || limit < 1) {
    throw new RangeError('Top-choice export limit must be a positive whole number.');
  }
  const scores = new Map(rankings.map((ranking) => [ranking.paperId, ranking.score]));
  return papers
    .filter((paper) => decisions[paper.id]?.status === 'interested')
    .sort(
      (left, right) =>
        (scores.get(right.id) ?? Number.NEGATIVE_INFINITY) -
          (scores.get(left.id) ?? Number.NEGATIVE_INFINITY) ||
        left.id.localeCompare(right.id, undefined, { numeric: true }),
    )
    .slice(0, limit);
}

/**
 * Starts a browser download and promptly releases the object URL.
 */
export function downloadCsv(csvText: string, filename: string): void {
  if (
    typeof document === 'undefined' ||
    typeof URL.createObjectURL !== 'function' ||
    typeof URL.revokeObjectURL !== 'function'
  ) {
    throw new Error('CSV downloads are only available in a browser.');
  }
  if (filename.trim() === '') throw new TypeError('A download filename is required.');

  const url = URL.createObjectURL(new Blob([csvText], { type: 'text/csv;charset=utf-8' }));
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
